import React, { memo, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ScommesseTableRowConditional } from 'components/scommesseTable/scommesseTableRowConditional';
import { ScommesseTableTabs } from 'components/scommesseTable/scommesseTableTabs';
import { ScommesseTableTitle } from 'components/scommesseTable/scommesseTableTitle';
import { SkeletonArea } from 'components/skeletonArea';
import { TooltipPosition } from 'components/tooltip';
import { GroupContext } from 'context/Group';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import {
  useAvvenimentoList,
  useDescrizioniScommesse,
  useFilteredAvvenimentoList,
  useInfoTipoList,
} from 'features/sport/hooks';
import { useVirtualDom } from 'hooks';
import { getDateFnsLocale } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { useTypedSelector } from 'lib/centralStore';
import { ItemWithTranslation, SportsPointerAvvenimentoDto } from 'types/swagger';
import { isTruthy } from 'utility/functions';
import { SportScommesseRenderConditional } from 'features/sport/components/sportScommesseRenderConditional';
import styles from './SportTableScommesse.module.scss';

export type SportTableScommesseProps = ItemWithTranslation<{ title?: string; gruppo?: string }> & {
  avvenimenti: Array<SportsPointerAvvenimentoDto>;
  infoTipoScommessaKeyList: (number | string)[];
  hasHeaderScommessa?: boolean;
  hasBorderSide?: boolean;
  showTableTitle?: boolean;
  isManifestazioneTemplate?: boolean;
  showTitle?: boolean;
  isTopTemplate?: boolean;
  isFirstAvvenimento?: boolean;
  hasMultipleLines?: boolean;
  selectedIndexInfoTipoScommessa?: number;
  showOnlyBilanciata?: boolean;
  className?: string;
  sortAvvenimenti?: boolean;
  iconSrc?: string;
  showTitleManifOnEveryRow?: boolean;
  suppressLoader?: boolean;
  gtagAction?: () => void;
};

export const SportTableScommesse = memo(function SportTableScommesse({
  title,
  avvenimenti,
  infoTipoScommessaKeyList,
  hasHeaderScommessa,
  hasBorderSide,
  showTableTitle,
  isManifestazioneTemplate,
  showTitle,
  isTopTemplate,
  selectedIndexInfoTipoScommessa,
  showOnlyBilanciata,
  className,
  sortAvvenimenti = false,
  titleTrKey,
  gruppo,
  gruppoTrKey,
  iconSrc,
  showTitleManifOnEveryRow,
  suppressLoader,
  gtagAction,
}: SportTableScommesseProps) {
  const { isMobile } = useWindowSize();

  const descrizioni = useDescrizioniScommesse(infoTipoScommessaKeyList);
  const { infoTipoScommessaList } = useInfoTipoList(infoTipoScommessaKeyList);
  const [selectedMobileTipoScommessa, setSelectedMobileTipoScommessa] = useState(0);

  const infoTipoScommessa = infoTipoScommessaList[selectedIndexInfoTipoScommessa ?? selectedMobileTipoScommessa];

  const wEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);
  const isCartOpen = useTypedSelector(selectIsOpenCarrello);

  const avvenimentiEsposti = useAvvenimentoList(
    (avvenimenti ?? []).filter(({ key }) => !!key).map(({ key }) => key!),
    sortAvvenimenti
  );
  const sortedAvvenimenti = useFilteredAvvenimentoList(avvenimentiEsposti);

  useEffect(() => {
    setSelectedMobileTipoScommessa(0);
  }, [infoTipoScommessaKeyList]);

  const isEstesa = useMemo(() => {
    if (!wEstesa) return false;
    if (!isManifestazioneTemplate) return false;

    const hasVisualizzazioneEstesa = infoTipoScommessaList?.some((item) =>
      [
        VisualizationEnum.EsitiOverThree,
        VisualizationEnum.SingleInfoAggEsitiUnderThree,
        VisualizationEnum.SingleInfoAggEsitiOverThree,
      ].includes(item.tipoVisualizzazione)
    );
    if (!hasVisualizzazioneEstesa) return false;

    return true;
  }, [wEstesa, isManifestazioneTemplate, infoTipoScommessaList]);

  const { keys, heights, wrapperRef } = useVirtualDom<HTMLDivElement>({
    uids: sortedAvvenimenti.map((x) => `${x.key}`),
  });

  return (
    <div
      ref={wrapperRef}
      data-idx={selectedIndexInfoTipoScommessa ?? selectedMobileTipoScommessa}
      className={classNames(styles.table, className, {
        [styles.titleFullLength]: showTitleManifOnEveryRow,
        ['extended']: isEstesa,
      })}
    >
      {showTableTitle && (
        <ScommesseTableTabs
          key={`tabs-${isMobile}`}
          activeIndex={selectedMobileTipoScommessa}
          descriptions={descrizioni}
          onSelectedItem={(selectedTipoScommessaIndex) => setSelectedMobileTipoScommessa(selectedTipoScommessaIndex)}
        />
      )}
      {sortedAvvenimenti?.map((avvenimento, index) => {
        const isSameAsPreviousDate =
          index !== 0 && isSameDay(new Date(avvenimento.dataOra), new Date(sortedAvvenimenti[index - 1].dataOra));
        const { descrizione: labelDx, descrizioneTrKey: labelDxTrKey } =
          descrizioni?.[selectedIndexInfoTipoScommessa ?? selectedMobileTipoScommessa] ?? {};

        let titleLabel: string | undefined = undefined;
        let titleTrKeyLabel: string | undefined = undefined;

        if (title && index === 0 && showTitle) {
          titleLabel = title!;
          titleTrKeyLabel = titleTrKey;
        }
        if (showTitleManifOnEveryRow) {
          titleLabel = avvenimento.descrizioneManifestazione ?? '';
          titleTrKeyLabel = avvenimento.descrizioneManifestazioneTrKey;
        }

        if (keys?.includes(`${avvenimento?.key}`)) {
          return (
            <div data-uid={avvenimento.key} key={`${avvenimento.key}-${index}`}>
              {!isSameAsPreviousDate && isManifestazioneTemplate && (
                <ScommesseTableTitle
                  labelSx={
                    !isTopTemplate
                      ? format(new Date(avvenimento.dataOra), 'd MMMM', { locale: getDateFnsLocale() })
                      : ''
                  }
                  isEstesa={isEstesa}
                  labelDx={labelDx}
                  labelDxTrKey={labelDxTrKey}
                  className={classNames({ [styles.hidden]: !isMobile, [styles.alignToLeft]: !isTopTemplate })}
                  tooltipText={infoTipoScommessa?.toolTip}
                  tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                  tooltipPosition={isMobile ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                  infoTipoScommessaKey={infoTipoScommessa?.key}
                />
              )}
              <ScommesseTableRowConditional
                index={index}
                title={titleLabel}
                titleTrKey={titleTrKeyLabel}
                hasBorderSide={hasBorderSide}
                avvenimentoKey={avvenimento.key}
                infoTipoScommessaKey={infoTipoScommessa?.key}
                hasHeaderScommessa={hasHeaderScommessa}
                hasMultipleLines={infoTipoScommessa?.tipoVisualizzazione >= 4 ? true : false}
                showOnlyHours={isManifestazioneTemplate && isMobile}
                isEstesa={isEstesa}
                gruppo={gruppo && index === 0 && showTitle ? gruppo : undefined}
                gruppoTrKey={gruppoTrKey && index === 0 && showTitle ? gruppoTrKey : undefined}
                iconSrc={iconSrc && index === 0 && showTitle ? iconSrc : undefined}
              >
                {infoTipoScommessaKeyList?.map((infoTipoScommessaKey: string | number, index) => {
                  const key = `${avvenimento.key}-${infoTipoScommessaKey}`;

                  if (avvenimento && infoTipoScommessaKey) {
                    if (String(infoTipoScommessaKey).startsWith('group-')) {
                      const tipoScommessaIds = String(infoTipoScommessaKey).replaceAll('group-', '').split('_') ?? [];

                      return (
                        <GroupContext.Provider
                          key={key}
                          value={{
                            avvenimentoKey: `${avvenimento.key}`,
                            scommessaGroupKey: String(infoTipoScommessaKey),
                          }}
                        >
                          <div
                            className={classNames(styles.quoteGroup, {
                              [styles.cartOpen]: isCartOpen,
                              [styles.estesa]: isEstesa,
                            })}
                            data-idx={index}
                          >
                            {isEstesa && (
                              <ScommesseTableTitle
                                infoTipoScommessaKey={String(infoTipoScommessaKey)}
                                scommessaGroupKey={String(infoTipoScommessaKey)}
                              />
                            )}
                            <div className={styles.scommesseContainer}>
                              {tipoScommessaIds.map((tipoScommessaId) => (
                                <SportScommesseRenderConditional
                                  key={`${key}-${tipoScommessaId}`}
                                  scommessaKey={`${avvenimento.key}-${tipoScommessaId}`}
                                  isManifestazioneTemplate={false}
                                  showOnlyBilanciata={showOnlyBilanciata}
                                  isEstesa={isEstesa}
                                  containerClassName={styles.noMargin}
                                  hasHeader={isEstesa}
                                />
                              ))}
                            </div>
                          </div>
                        </GroupContext.Provider>
                      );
                    }

                    return (
                      <SportScommesseRenderConditional
                        key={`${key}-${index}`}
                        index={index}
                        hasTitle={isEstesa}
                        isEstesa={isEstesa}
                        scommessaKey={`${avvenimento.key}-${infoTipoScommessaKey}`}
                        containerClassName={classNames(styles.quoteGroup, { [styles.cartOpen]: isCartOpen })}
                        showOnlyBilanciata={showOnlyBilanciata}
                        isManifestazioneTemplate={false}
                        gtagAction={gtagAction}
                        hasHeader={isEstesa}
                      />
                    );
                  }

                  return <React.Fragment key={key} />;
                })}
              </ScommesseTableRowConditional>
            </div>
          );
        }
        return (
          <SkeletonArea
            uid={avvenimento.key}
            key={`${avvenimento.key}-${index}`}
            style={{ height: heights[avvenimento.key!], margin: 0 }}
            className={classNames(styles.skeletonRow, {
              [styles.hidden]: suppressLoader && !isTruthy(infoTipoScommessaKeyList?.length),
            })}
          />
        );
      })}
    </div>
  );
});
